<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { createSortable, type CreateSortableInput } from "@dnd-kit/svelte/sortable";
	import { A, Input } from "flowbite-svelte-v1";
	import { type Snippet } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { v4 } from "uuid";
	import type { Question } from "#gen/forms/v1/poll_pb.js";
	import { ImageSchema, PollAnswerSchema } from "#gen/forms/v1/poll_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import { DEFAULT_POLL_ANSWER_IMAGE } from "#lib/components/flow/editor/appearance/constants.js";
	import EditImage from "#lib/components/images/EditImage.svelte";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import DescriptionInput from "#lib/components/page/components/form/DescriptionInput.svelte";
	import TitleInput from "#lib/components/page/components/form/TitleInput.svelte";
	import Button from "#lib/ui/Button/index.js";

	interface Props extends CreateSortableInput {
		total: number;
		question: Question;
		active?: boolean;
		children?: Snippet;
		onRemove?: (question: string) => void;
		onDuplicate?: (question: Question) => void;
		onMoveUp?: (question: Question, index: number) => void;
		onMoveDown?: (question: Question, index: number) => void;
		onClick?: (e: MouseEvent) => void;
		onKeydown?: (e: KeyboardEvent) => void;
		onInput?: (question: Question) => void;
		onChange?: (question: Question) => void;
	}

	let { question = $bindable(), id, index, total, active = false, onRemove, onDuplicate, onMoveUp, onMoveDown, onClick, onKeydown, onInput, onChange, ...rest }: Props = $props();

	const { attach, attachHandle, isDragging } = $derived(
		createSortable({
			...rest,
			get id() {
				return id;
			},
			get index() {
				return index;
			},
			get data() {
				return question;
			},
		}),
	);

	function handleAddOption() {
		question.pollAnswers = [...question.pollAnswers, create(PollAnswerSchema, { value: v4(), primaryLabel: `Option ${question.pollAnswers.length + 1}`, image: create(ImageSchema, DEFAULT_POLL_ANSWER_IMAGE) })];
		onChange?.(question);
	}

	function handleRemoveOption(index: number) {
		question.pollAnswers = question.pollAnswers.filter((_, i) => i !== index);
		onChange?.(question);
	}

	function handleImageChange(e: CustomEvent<ImagePickerChangeEvent>, index: number) {
		const option = question.pollAnswers[index];
		if (!option) {
			return;
		}
		option.image = create(ImageSchema, { imageUrl: e.detail.value });
		handleChange();
	}

	function handleRemove(e: Event) {
		e.stopImmediatePropagation();
		onRemove?.(question.id);
	}

	function handleDuplicate(e: Event) {
		e.stopImmediatePropagation();
		onDuplicate?.(question);
	}

	function handleMoveUp(e: Event) {
		e.stopImmediatePropagation();
		onMoveUp?.(question, index as number);
	}

	function handleMoveDown(e: Event) {
		e.stopImmediatePropagation();
		onMoveDown?.(question, index as number);
	}

	function handleInput() {
		onInput?.(question);
	}

	function handleChange() {
		onChange?.(question);
	}
</script>

<div {@attach attach} id="question-{question.id}" onclick={onClick} role="button" onkeydown={onKeydown} tabindex="-1">
	<div class={twMerge("group flex items-center rounded-lg  border bg-white px-3 py-6 hover:border-gray-400", active && "border-primary-700 hover:border-primary-700")}>
		<div {@attach attachHandle} aria-label="drag-handle for {question.name}" class={twMerge("mr-2 w-8 flex-none opacity-0 group-hover:opacity-100", (isDragging || active) && "opacity-100")} data-action-name="poll-question-reposition" data-action-title={question.name}>
			<Icon iconSet="mingcute" name="dots-line" />
		</div>
		<div class="flex grow flex-col gap-2">
			<div class="flex items-start gap-3 pb-3">
				<div class="rounded-lg p-2 text-center" style="background-color: #D9D2E9;">
					<Icon name="poll" iconSet="gg" class="m-auto h-5 w-5" />
				</div>
				<div class="flex grow flex-col">
					<div>
						<TitleInput bind:value={question.name} name={question.name} required={true} onChange={handleInput} onCommit={handleChange} />
					</div>
					<DescriptionInput bind:value={question.description} onChange={handleInput} onCommit={handleChange} />
				</div>
			</div>
			<div class="flex flex-col gap-4">
				{#each question.pollAnswers as option, i (i)}
					{#if i !== 0 && question.pollAnswers.length === 2}
						<div class="w-full">
							<Input bind:value={question.alternativeText} placeholder="VS (Optional)" oninput={handleInput} onchange={handleChange} />
						</div>
					{/if}
					<div class="flex w-full items-start gap-6 rounded-lg border p-6">
						<div class="h-9 w-9 flex-none">
							<EditImage class="h-9 w-9 border-0 bg-white" dropzoneClass="w-9 h-9 border-solid border-gray-300" value={option.image?.imageUrl} on:change={e => handleImageChange(e, i)} aspectRatio={1} />
						</div>
						<div class="flex grow flex-col gap-2">
							<Input bind:value={option.primaryLabel} placeholder="Primary text" required={true} oninput={handleInput} onchange={handleChange} />
							<Input bind:value={option.secondaryLabel} placeholder="Subtext (Optional)" required={true} oninput={handleInput} onchange={handleChange} />
						</div>
						<div class="flex-none py-2">
							<Button color="none" data-action-name="Remove option" class="p-0" disabled={question.pollAnswers.length <= 2} on:click={() => handleRemoveOption(i)}>
								<Icon name="x-mark" class="h-5 w-5" />
							</Button>
						</div>
					</div>
				{/each}
				<A class="text-sm" onclick={handleAddOption}>Add option</A>
			</div>
		</div>
		<div class="mx-1 w-8 flex-none opacity-0 group-hover:opacity-100" class:!opacity-100={active}>
			<Button on:click={handleMoveUp} color="light" class="mb-1 border-0 p-2!" data-action-name="poll-question-move-up" data-action-title={question.name} disabled={index === 0}>
				<span class="sr-only">Move up</span>
				<Icon name="chevron-up" />
			</Button>
			<Button on:click={handleDuplicate} color="light" class="mb-1 border-0 p-2!" data-action-name="poll-question-duplicate" data-action-title={question.name}>
				<span class="sr-only">Duplicate</span>
				<Icon name="document-duplicate" />
			</Button>
			<Button on:click={handleRemove} color="light" class="border-0 p-2!" data-action-name="poll-question-delete" data-action-title={question.name} disabled={total === 1}>
				<span class="sr-only">Delete</span>
				<Icon name="trash" />
			</Button>
			<Button on:click={handleMoveDown} color="light" class="mb-1 border-0 p-2!" data-action-name="poll-question-move-down" data-action-title={question.name} disabled={index === total - 1}>
				<span class="sr-only">Move down</span>
				<Icon name="chevron-down" />
			</Button>
		</div>
	</div>
</div>
